import * as t from 'io-ts'
import React, {useState} from 'react'
import {NavLink} from 'react-router-dom'
import {del} from 'object-path-immutable'
import {gql} from '@apollo/client'
import {isRight} from 'fp-ts/lib/Either'

import {SSOAccessMappingCodec, SSOAccessMapping} from '../../common/types'
import {BetaFlagMarker} from '../../components/feature-flags'
import {FormSpacer} from '../../components/form'
import {TextInput} from '../../components/library/form'
import {Icon} from '../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../components/library/list'
import styled from 'styled-components'
import {Loader} from '../../components/loader'
import {
  IconHeading,
  Section,
  SectionContent,
  SectionInfo
} from '../configuration/components'
import {
  useListAppClientsQuery,
  useListGroupsQuery,
  useUpdateSsoMappingMutation
} from './generated/sso'
import reporter from 'io-ts-reporters'
import {
  DropdownBody,
  DropdownItem
} from '../../components/library/dropdown/content'
import {Modal} from '../../components/library/modal/modal'
import {Checkbox} from '../../components/library/form/checkbox'
import {Dropdown} from '../../components/library/dropdown/dropdown'
import {Button} from '../../components/library/buttons'

const SmallText = styled.p`
  color: ${props => props.theme.grey50};
  font-size: ${props => props.theme.fontSizeXS};
  margin-bottom: 10px;
  a {
    color: ${props => props.theme.blue};
  }
`

const DropdownWrapper = styled.a`
  border-radius: 4px;
  background-color: ${p => p.theme.colors.grey3};
  display: block;
`

const LightTextSmall = styled.span`
  color: ${p => p.theme.colors.grey10};
  font-size: ${p => p.theme.fontSizeS};
`

export const ManageSSOAuth = ({
  organisationId,
  canView,
  canEdit
}: {
  organisationId: string
  canView: boolean
  canEdit: boolean
}) => {
  const {data: appClientData, loading: appClientLoading} =
    useListAppClientsQuery({
      variables: {organisationId}
    })
  const {data: groupData, loading: groupLoading} = useListGroupsQuery({
    variables: {organisationId}
  })

  const appClients = appClientData?.appClients?.nodes ?? []
  const groups = groupData?.groups?.nodes ?? []

  if (!canView) return <NoResultsPermissions />
  if (appClientLoading || groupLoading) return <Loader />

  return (
    <>
      <header>
        <IconHeading>
          <Icon name="padlock" />
          <a href="#mapping">Single Sign-On (SSO)</a> <BetaFlagMarker />
        </IconHeading>
      </header>

      <SectionInfo>
        <p>
          Configured{' '}
          <a href="https://documentation.atomic.io/advanced/SSO-integration">
            SSO clients
          </a>{' '}
          for this organisation
        </p>
      </SectionInfo>

      {appClients.map(client => {
        return (
          <AppClient
            key={`app-client-${client.appClientId}`}
            appClientId={client.appClientId}
            appClientName={client.name}
            ssoClaimMapping={client.ssoClaimMapping}
            organisationId={organisationId}
            canEdit={canEdit}
            groups={groups}
          />
        )
      })}
    </>
  )
}

const AppClient = ({
  appClientId,
  appClientName,
  ssoClaimMapping,
  organisationId,
  canEdit,
  groups
}: {
  appClientId: string
  appClientName: string | undefined | null
  ssoClaimMapping: unknown
  organisationId: string
  canEdit: boolean
  groups: {id: string; name: string}[]
}) => {
  const [updateSsoMappingMutation] = useUpdateSsoMappingMutation()
  const [addModal, setAddModal] = useState(false)

  const decoded = t
    .union([SSOAccessMappingCodec, t.null])
    .decode(ssoClaimMapping)
  if (!isRight(decoded)) {
    console.warn('Invalid mapping', {
      errors: reporter.report(decoded),
      mapping: ssoClaimMapping
    })
    return null
  }
  const mappings = Object.entries(decoded.right?.groupMapping ?? {}).map(
    ([name, value], index: number) => ({
      id: index,
      name,
      groupIds: value
    })
  )

  const columns: ListColumns<
    {
      id: string | number
      name: string
      groupIds: string | string[]
    },
    'id'
  > = [
    {id: 'ssoRole', header: 'SSO value', accessor: r => r.name},
    {
      id: 'workbenchRole',
      header: 'Workbench groups',
      accessor: r =>
        Array.isArray(r.groupIds)
          ? r.groupIds.map(m => groups.find(g => g.id === m)?.name).join(', ')
          : groups.find(g => g.id === r.groupIds)?.name ?? r.groupIds
    }
  ]

  return (
    <>
      {addModal ? (
        <EditModal
          existingMappings={decoded.right}
          appClientId={appClientId}
          organisationId={organisationId}
          canEdit={canEdit}
          groups={groups}
          setModal={setAddModal}
          updateSsoMappingMutation={updateSsoMappingMutation}
        />
      ) : null}
      <Section>
        <IconHeading style={{marginBottom: '20px'}}>
          {appClientName}
        </IconHeading>
        <SectionInfo style={{marginBottom: '5px'}}>ID</SectionInfo>
        <SmallText>{appClientId}</SmallText>
        <SectionInfo style={{marginBottom: '5px'}}>Mappings</SectionInfo>
        <div style={{display: 'flex', justifyContent: 'space-between'}}>
          <SmallText>
            Map SSO values to Workbench&nbsp;
            <NavLink to={`/${organisationId}/permissions/groups`}>
              groups
            </NavLink>{' '}
            to automate Workbench access.
          </SmallText>
          <Button
            onClick={() => {
              setAddModal(true)
            }}
          >
            Add mapping
          </Button>
        </div>
        <SectionContent style={{marginTop: '10px'}}>
          <List
            bleed="#f2f2f2"
            columns={columns}
            rows={mappings}
            renderOverflow={(mapping, closeDropdown) => (
              <SSOMappingOverflow
                mapping={mapping}
                existingMappings={decoded.right}
                appClientId={appClientId}
                organisationId={organisationId}
                closeDropdown={closeDropdown}
                canEdit={canEdit}
                groups={groups}
                updateSsoMappingMutation={updateSsoMappingMutation}
              />
            )}
          />
        </SectionContent>
      </Section>
    </>
  )
}

const SSOMappingOverflow = ({
  mapping,
  existingMappings,
  appClientId,
  organisationId,
  closeDropdown,
  canEdit,
  groups,
  updateSsoMappingMutation
}: {
  mapping: {groupIds: string | string[]; name: string}
  existingMappings: SSOAccessMapping | undefined | null
  appClientId: string
  organisationId: string
  closeDropdown: () => void
  canEdit: boolean
  groups: {id: string; name: string}[]
  updateSsoMappingMutation: (options: {
    variables: {
      appClientId: string
      organisationId: string
      mapping?: unknown
    }
  }) => Promise<unknown>
}) => {
  const [editModal, setEditModal] = useState(false)

  return (
    <>
      {editModal ? (
        <EditModal
          mapping={mapping}
          existingMappings={existingMappings}
          appClientId={appClientId}
          organisationId={organisationId}
          closeDropdown={closeDropdown}
          canEdit={canEdit}
          groups={groups}
          setModal={setEditModal}
          updateSsoMappingMutation={updateSsoMappingMutation}
        />
      ) : null}
      <DropdownBody>
        {canEdit ? (
          <>
            <DropdownItem>
              <a
                onClick={() => {
                  setEditModal(true)
                }}
              >
                Edit
              </a>
            </DropdownItem>
            <DropdownItem>
              <a
                onClick={() => {
                  updateSsoMappingMutation({
                    variables: {
                      appClientId,
                      organisationId,
                      mapping: del(
                        existingMappings,
                        `groupMapping.${mapping.name}`
                      )
                    }
                  })
                  setEditModal(false)
                  closeDropdown()
                }}
              >
                Delete
              </a>
            </DropdownItem>
          </>
        ) : null}
      </DropdownBody>
    </>
  )
}

const EditModal = ({
  closeDropdown,
  setModal,
  mapping,
  existingMappings,
  groups,
  updateSsoMappingMutation,
  appClientId,
  organisationId
}: {
  setModal: (value: boolean) => void
  mapping?: {groupIds: string | string[]; name: string}
  existingMappings: SSOAccessMapping | undefined | null
  appClientId: string
  organisationId: string
  closeDropdown?: () => void
  canEdit: boolean
  groups: {id: string; name: string}[]
  updateSsoMappingMutation: (options: {
    variables: {
      appClientId: string
      organisationId: string
      mapping?: unknown
    }
  }) => Promise<unknown>
}) => {
  const [mappedGroups, setMappedGroups] = useState(
    mapping
      ? Array.isArray(mapping.groupIds)
        ? mapping.groupIds
        : [mapping.groupIds]
      : []
  )
  const [ssoValue, setSsoValue] = useState(mapping?.name ?? '')

  return (
    <Modal
      handleClose={() => {
        if (closeDropdown) closeDropdown()
        setModal(false)
      }}
      title={mapping ? 'Edit mapping' : 'Add mapping'}
    >
      <div>
        <TextInput
          label="SSO value.."
          upLabel="SSO value"
          value={ssoValue}
          onChange={value => setSsoValue(value)}
        />
        <FormSpacer />
        <Dropdown
          trigger={(handleClick, ref) => (
            <DropdownWrapper
              onClick={handleClick}
              ref={ref as React.Ref<HTMLAnchorElement>}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 20px'
                }}
              >
                <div>
                  <LightTextSmall>Workbench Groups</LightTextSmall>
                  <br />
                  {mappedGroups
                    .map(m => groups.find(g => g.id === m)?.name)
                    .join(', ')}
                </div>
                <div
                  style={{
                    float: 'right',
                    marginRight: '25px'
                  }}
                >
                  <Icon name="chevron" rotate={90} />
                </div>
              </div>
            </DropdownWrapper>
          )}
        >
          {() => (
            <>
              <DropdownBody>
                {groups?.map(g => {
                  return (
                    <DropdownItem key={g.id}>
                      <Checkbox
                        style={{marginLeft: '10px'}}
                        key={g.id}
                        label={g.name}
                        checked={mappedGroups.includes(g.id)}
                        onChange={checked => {
                          if (checked) {
                            setMappedGroups(cur => [
                              ...new Set(cur ? [...cur, g.id] : [g.id])
                            ])
                          } else {
                            setMappedGroups(cur =>
                              (cur ?? []).filter(id => id !== g.id)
                            )
                          }
                        }}
                      />
                    </DropdownItem>
                  )
                })}
              </DropdownBody>
            </>
          )}
        </Dropdown>
        <div style={{paddingTop: '20px'}}>
          <Button
            style={{marginRight: '10px'}}
            appearance="secondary"
            onClick={() => {
              setModal(false)
              if (closeDropdown) closeDropdown()
            }}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={() => {
              // remove the current mapping in case we changed the key
              // which is the sso value.
              const mappingsMinusCurrent = mapping
                ? del(existingMappings, `groupMapping.${mapping.name}`)
                : existingMappings
              const updateMapping: SSOAccessMapping = {
                ...mappingsMinusCurrent,
                groupMapping: {
                  ...mappingsMinusCurrent?.groupMapping,
                  [ssoValue]: mappedGroups
                }
              }
              updateSsoMappingMutation({
                variables: {
                  appClientId,
                  organisationId,
                  mapping: updateMapping
                }
              })
              setModal(false)
              if (closeDropdown) closeDropdown()
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ManageSSOAuth.fragments = {
  appClient: gql`
    fragment ManageSSOAuth on AppClient {
      nodeId
      name
      appClientId
      created
      updated
      ssoClaimMapping
    }
  `
}

gql`
  query ListAppClients($organisationId: String!) {
    appClients(condition: {organisationId: $organisationId}) {
      nodes {
        ...ManageSSOAuth
      }
    }
  }

  mutation UpdateSsoMapping(
    $appClientId: String!
    $organisationId: String!
    $mapping: JSON
  ) {
    updateAppClient(
      input: {
        appClientId: $appClientId
        organisationId: $organisationId
        patch: {ssoClaimMapping: $mapping}
      }
    ) {
      appClient {
        ...ManageSSOAuth
      }
    }
  }

  query ListGroups($organisationId: String!) {
    groups(condition: {organisationId: $organisationId}) {
      nodes {
        name
        id
      }
    }
  }

  ${ManageSSOAuth.fragments.appClient}
`
